import { apiFetch, apiSend, apiSendJson } from './client';
import type {
  AccessPermission,
  Group,
  GroupAccessGrant,
  GroupDetail,
  GroupMember,
  GroupRole,
  GroupSummary,
} from './types';

export function listGroups(): Promise<Group[]> {
  return apiFetch<Group[]>('/api/groups');
}

/** Every group name, for the sharing picker. Group admins only. */
export function getGroupDirectory(): Promise<GroupSummary[]> {
  return apiFetch<GroupSummary[]>('/api/groups/directory');
}

export function getGroup(id: string): Promise<GroupDetail> {
  return apiFetch<GroupDetail>(`/api/groups/${id}`);
}

export function createGroup(name: string, description?: string): Promise<Group> {
  return apiSendJson<Group>('/api/groups', 'POST', { name, description });
}

export function updateGroup(id: string, name: string, description?: string): Promise<void> {
  return apiSend(`/api/groups/${id}`, 'PUT', { name, description });
}

export function deleteGroup(id: string): Promise<void> {
  return apiSend(`/api/groups/${id}`, 'DELETE');
}

export function addGroupMember(
  groupId: string,
  userId: string,
  role: GroupRole,
  displayName?: string,
): Promise<GroupMember> {
  return apiSendJson<GroupMember>(`/api/groups/${groupId}/members`, 'POST', { userId, role, displayName });
}

export function updateGroupMemberRole(groupId: string, userId: string, role: GroupRole): Promise<void> {
  return apiSend(`/api/groups/${groupId}/members/${encodeURIComponent(userId)}`, 'PUT', { role });
}

export function removeGroupMember(groupId: string, userId: string): Promise<void> {
  return apiSend(`/api/groups/${groupId}/members/${encodeURIComponent(userId)}`, 'DELETE');
}

export function listGrants(groupId: string, includeRevoked = false): Promise<GroupAccessGrant[]> {
  const query = includeRevoked ? '?includeRevoked=true' : '';
  return apiFetch<GroupAccessGrant[]>(`/api/groups/${groupId}/grants${query}`);
}

/** "This group allows `grantedToGroupId` to view/edit its QR codes." */
export function createGrant(
  groupId: string,
  grantedToGroupId: string,
  permission: AccessPermission,
): Promise<GroupAccessGrant> {
  return apiSendJson<GroupAccessGrant>(`/api/groups/${groupId}/grants`, 'POST', {
    grantedToGroupId,
    permission,
  });
}

export function revokeGrant(groupId: string, grantId: string): Promise<void> {
  return apiSend(`/api/groups/${groupId}/grants/${grantId}`, 'DELETE');
}
